import * as D from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * Drawer — panel lateral. La pieza que más faltaba.
 *
 * En la auditoría del 2026-09-03 el producto tenía **95 modales y 45
 * confirmaciones, y cero paneles laterales**. Eso obliga a resolver con una
 * interrupción cosas que no la necesitan: revisar diez solicitudes significaba
 * abrir y cerrar diez modales, perdiendo la cola cada vez.
 *
 * Cuándo un panel y no un modal: **cuando lo que hay detrás sigue importando**.
 * El detalle de una fila, la edición de un registro de una tabla, la ficha de
 * una persona del directorio. El modal se reserva para dos casos —confirmar
 * algo irreversible y una barrera legal que de verdad bloquea—, que son unos
 * quince en todo el producto, no ciento cuarenta.
 *
 * Se apoya en Radix por el comportamiento, no por el aspecto: foco atrapado y
 * devuelto, Escape, `aria-modal`, y el `inert` del fondo. Escribir eso a mano
 * bien es caro, y el portal ya sabe lo que cuesta no tenerlo: hasta este mismo
 * día su modal no atrapaba el foco. Todo lo visual es nuestro.
 */

const panel = cva(
  'fixed z-panel flex flex-col bg-surface shadow-overlay focus:outline-none',
  {
    variants: {
      lado: {
        derecha: 'inset-y-0 right-0 h-full border-l border-line anim-panel-derecha',
        // En móvil el panel entra desde abajo: alcanzar el pulgar es más fácil
        // que alcanzar el borde derecho de una pantalla de 390 px.
        abajo: 'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-lg border-t border-line anim-panel-abajo',
      },
      ancho: {
        sm: 'w-full sm:max-w-md',
        md: 'w-full sm:max-w-xl',
        lg: 'w-full sm:max-w-3xl',
      },
    },
    defaultVariants: { lado: 'derecha', ancho: 'md' },
  },
);

export function Drawer({
  abierto, onCambio, titulo, descripcion, pie, lado, ancho, children,
}: VariantProps<typeof panel> & {
  abierto: boolean;
  onCambio: (v: boolean) => void;
  titulo: string;
  /** Una línea sobre qué es esto. Va al `aria-describedby` del panel, así que
   *  el lector de pantalla la lee justo después del título. */
  descripcion?: string;
  pie?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <D.Root open={abierto} onOpenChange={onCambio}>
      <D.Portal>
        {/* El velo es tenue a propósito: el panel NO tapa lo de detrás, lo
            complementa. Un velo oscuro diría «olvídate de la lista», que es
            justo lo contrario de para qué existe un panel. */}
        <D.Overlay
          className="anim-velo fixed inset-0 z-panel bg-brand/25"
        />
        <D.Content className={cn(panel({ lado, ancho }))}>
          <header className="flex items-start gap-3 border-b border-line px-5 py-4">
            <div className="min-w-0 flex-1">
              <D.Title className="text-5 font-bold leading-tight text-brand-ink">{titulo}</D.Title>
              {descripcion && (
                <D.Description className="mt-1 text-3 text-muted">{descripcion}</D.Description>
              )}
            </div>
            <D.Close asChild>
              <Button variant="ghost" size="sm" iconOnly aria-label="Cerrar panel">
                <i className="bi bi-x-lg" aria-hidden="true" />
              </Button>
            </D.Close>
          </header>

          {/* El cuerpo es lo único que se desplaza: cabecera y pie quedan
              anclados, así que la acción principal nunca se va de la vista por
              mucho contenido que haya. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {pie && (
            <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">
              {pie}
            </footer>
          )}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
